import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiClient } from '@/services/api-client';
import type { PlayerStatsResponse, H2HStatsResponse, ApiError, Game } from '@/services/api-client';

interface UsePlayerStatsReturn {
  stats: PlayerStatsResponse | H2HStatsResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isAuthenticated: boolean;
}

/**
 * Hook to fetch player statistics for a game
 * @param gameId - The ID of the game
 * @param game - The game data (used to determine challenge type)
 * @returns Player statistics, loading state, error state, and refetch function
 */
export function usePlayerStats(gameId: string | null, game: Game | null): UsePlayerStatsReturn {
  const { isSignedIn } = useAuth();
  const [stats, setStats] = useState<PlayerStatsResponse | H2HStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    if (!gameId || !game) {
      setStats(null);
      setError(null);
      return;
    }

    // If user is not signed in, don't fetch stats
    if (!isSignedIn) {
      setStats(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let statsData: PlayerStatsResponse | H2HStatsResponse;

      if (game.challengeType === 'head_to_head') {
        // Check if it's an H2H game (Diamond Duel)
        try {
          statsData = await apiClient.getH2HStats(gameId);
        } catch {
          // H2H endpoint might not be implemented yet, use placeholder data
          console.warn('H2H stats endpoint not available, using placeholder data');
          statsData = {
            gameId,
            userId: 'placeholder',
            stats: {
              activeGames: 0,
              winPercentage: 0,
              wins: 0,
              losses: 0,
              averageGamesPlayed: 0,
            },
          } as H2HStatsResponse;
        }
      } else {
        // Regular daily/weekly challenge game - call real API
        statsData = await apiClient.getPlayerStats(gameId);
      }

      setStats(statsData);
    } catch (err) {
      const apiError = err as ApiError;
      // Don't show error for 404 (user has no stats yet)
      if (apiError.status === 404) {
        // Return empty stats structure
        if (game.challengeType === 'head_to_head') {
          setStats({
            gameId: gameId,
            userId: '',
            stats: {
              activeGames: 0,
              winPercentage: 0,
              wins: 0,
              losses: 0,
              averageGamesPlayed: 0,
            },
          } as H2HStatsResponse);
        } else {
          setStats({
            gameId: gameId,
            userId: '',
            stats: {
              currentStreak: 0,
              averageScore: 0,
              successRate: 0,
              totalPlayed: 0,
              totalStars: 0, // IMA-97: Include total stars in fallback
            },
            currentChallenge: {
              id: '',
              puzzleName: '',
              puzzleNumber: 0,
              publishDate: '',
              playerStatus: 'not_started',
            },
          } as PlayerStatsResponse);
        }
      } else {
        setError(new Error(apiError.message || 'Failed to fetch player statistics'));
        setStats(null);
      }
    } finally {
      setLoading(false);
    }
  }, [gameId, game, isSignedIn]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
    isAuthenticated: !!isSignedIn,
  };
}